/**
 * React NodeView for `image` on the card-description + comment editor
 * surfaces. Bound by `buildCardImageExtension` (registered when
 * `surface === 'card'` / `'comment'`).
 *
 * Compared to `WikiImageNodeView` this is intentionally lean: no
 * lightbox, no cancel-upload orchestration (cards don't mount the wiki
 * `WikiEmbedOrchestrator`). Its jobs are (1) an instant in-body preview +
 * spinner while a dropped / picked image uploads, (2) a durable render
 * that resolves a fresh signed GET URL from `attachmentId` so the embed
 * survives a reload, and (3) a bounded render size.
 *
 * Branches, in priority order:
 *   0. FAILED — `uploadingFailed` is set on a placeholder. The dimmed blob
 *      preview stays in the body with "Upload failed" + Retry / Dismiss
 *      below it instead of vanishing; the buttons dispatch the card-embed
 *      window events and the OWNING composer (which retained the `File`)
 *      re-runs the upload against this same node or removes it.
 *   1. UPLOADING — `uploadingBlobUrl` is a live blob URL. Renders the
 *      local bytes dimmed with a centered spinner.
 *   2. UNAVAILABLE — orphan (no attachmentId + no src) OR the signed URL
 *      resolution failed and there's no usable fallback.
 *   3. SKELETON — `attachmentId` set, signed URL still resolving, no src
 *      yet.
 *   4. READY — renders `src` when present (fresh upload / legacy toolbar
 *      URL embed) and falls back to the signed URL resolved from
 *      `attachmentId`; `<img onError>` mints a fresh URL.
 *
 * Size cap: max-height 20rem, max-width 100%, width/height auto,
 * `object-fit: contain` — preserves aspect ratio for portrait +
 * landscape and never upscales a small image.
 */

import type { NodeViewProps } from '@tiptap/react'
import { NodeViewWrapper } from '@tiptap/react'
import { AlertCircle, ImageOff, Loader2, RotateCcw, X } from 'lucide-react'
import { type MouseEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AttachmentLightbox } from '@/components/AttachmentLightbox'
import {
  type CardEmbedUploadAction,
  dispatchCardEmbedUploadAction,
} from '@/editor/nodes/card-embed-upload-events'
import { UploadProgressBar } from '@/features/uploads/UploadProgressBar'
import { useImagePresignedUrl } from '@/features/wiki/hooks/use-image-presigned-url'
import { useUploadProgress } from '@/features/wiki/hooks/use-upload-guards'
import { externalImageLinkHref, isAllowedImageSrc } from '@/lib/allowed-image-origins'

import { ExternalImageLink } from './ExternalImageLink'

// The `prose img` top/bottom margin (the "extra space" around embedded
// images) is killed by a specificity-winning rule in `global.css` scoped
// to `[data-block-type="card-image"] img` — a `my-0` utility doesn't win
// because it sets the logical `margin-block`, not the physical margins the
// prose rule targets.
const IMG_CLASS = 'block h-auto max-h-96 w-auto max-w-full rounded-md'
const IMG_STYLE = { objectFit: 'contain' as const }

export function CardImageNodeView({ node }: NodeViewProps) {
  const { t } = useTranslation('uploads')
  const attachmentId = (node.attrs.attachmentId as string | null) ?? null
  // A `src` outside our own origins is never loaded as an image: it is drawn
  // as a link (or ignored when an attachment id can supply the real bytes).
  const rawSrc = (node.attrs.src as string | null) ?? null
  const srcAttr = isAllowedImageSrc(rawSrc) ? rawSrc : null
  const externalHref = attachmentId === null && !srcAttr ? externalImageLinkHref(rawSrc) : null
  const altAttr = (node.attrs.alt as string | null) ?? ''
  const uploadingBlobUrl = (node.attrs.uploadingBlobUrl as string | null) ?? null
  const placeholderId = (node.attrs.placeholderId as string | null) ?? null
  const uploadingFailed = Boolean(node.attrs.uploadingFailed)

  // Signed-URL hook — gated on `attachmentId` (disabled when null), so a
  // legacy toolbar-URL embed never pings the BE.
  const { url: signedUrl, isError, status, refresh } = useImagePresignedUrl(attachmentId)
  // Once a rendered `src` fails to load (expired signed URL from an old
  // save), stop preferring it and fall to the freshly-resolved signed URL.
  const [srcFailed, setSrcFailed] = useState(false)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  // The composer publishes byte progress under the placeholder id — the
  // only handle this NodeView has on the upload driving it. Null until
  // the first byte event, which the bar draws as indeterminate.
  const uploadProgress = useUploadProgress(placeholderId)

  // --- 0. FAILED branch. Checked before UPLOADING — a failed node still
  // carries its blob URL (the composer keeps it alive for this preview
  // until Dismiss / a successful Retry). Controls sit in a strip BELOW the
  // image, not overlaid: a small dropped image can't contain them.
  // Shared by the failed branch (retry / dismiss) and the uploading one
  // (cancel). Plain inline function, no hook, so the hook count stays
  // stable across the uploading ↔ failed ↔ ready flips.
  const dispatchAction = (action: CardEmbedUploadAction) => (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!placeholderId) return
    dispatchCardEmbedUploadAction(action, placeholderId)
  }

  if (uploadingBlobUrl && uploadingFailed && placeholderId) {
    return (
      <NodeViewWrapper
        as="div"
        data-block-type="card-image"
        data-state="error"
        className="my-1"
        contentEditable={false}
      >
        <span className="inline-block max-w-full align-baseline">
          <img
            src={uploadingBlobUrl}
            alt={altAttr || t('embed.failedLabel')}
            className={`${IMG_CLASS} opacity-40`}
            style={IMG_STYLE}
            draggable={false}
          />
          {/* `<span>`s throughout: the wrapper is inside a `<span>` (only
              phrasing content is valid there), and the label is content
              rather than a live region — the composer's sr-only region is
              the single announcement for a failed upload. */}
          <span className="mt-1.5 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-danger">
              <AlertCircle size={14} aria-hidden="true" />
              {t('embed.failedLabel')}
            </span>
            <button
              type="button"
              onClick={dispatchAction('retry')}
              onMouseDown={(e) => e.stopPropagation()}
              className="inline-flex min-h-6 items-center gap-1 rounded-md border border-border bg-bg-elevated px-2 py-1 text-xs font-medium text-fg hover:bg-bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <RotateCcw aria-hidden="true" size={12} />
              {t('embed.retry')}
            </button>
            <button
              type="button"
              onClick={dispatchAction('dismiss')}
              onMouseDown={(e) => e.stopPropagation()}
              className="inline-flex min-h-6 items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-fg-muted hover:bg-bg-muted hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              {t('embed.dismiss')}
            </button>
          </span>
        </span>
      </NodeViewWrapper>
    )
  }

  // --- 1. UPLOADING branch.
  if (uploadingBlobUrl) {
    return (
      <NodeViewWrapper
        as="div"
        data-block-type="card-image"
        data-state="uploading"
        className="my-1"
        contentEditable={false}
      >
        <span className="relative inline-block max-w-full align-baseline">
          <img
            src={uploadingBlobUrl}
            alt={altAttr || t('embed.uploading')}
            className={`${IMG_CLASS} opacity-50`}
            style={IMG_STYLE}
            draggable={false}
          />
          <output className="absolute inset-0 flex items-center justify-center" aria-live="polite">
            <Loader2
              size={28}
              aria-hidden="true"
              className="animate-spin text-white drop-shadow-md"
            />
            <span className="sr-only">{t('embed.uploading')}</span>
          </output>
          {/* Along the bottom of the preview rather than beneath it: the
           * image already reserves the space, so the bar costs no layout
           * and cannot shift the paragraph as it resolves. */}
          <span className="absolute inset-x-2 bottom-2 block">
            <UploadProgressBar progress={uploadProgress} label={t('progress.ariaLabelGeneric')} />
          </span>
          {/* Over the preview's top-end corner, the way the wiki video
           * panel places its own. Solid rather than ghosted: it sits on
           * an arbitrary photograph, so it needs its own ground to stay
           * legible on a light one and a dark one alike. */}
          {placeholderId ? (
            <button
              type="button"
              onClick={dispatchAction('cancel')}
              onMouseDown={(e) => e.stopPropagation()}
              className="absolute end-2 top-2 inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-full border border-border bg-bg-elevated text-fg shadow-sm hover:bg-danger hover:text-danger-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              aria-label={t('embed.cancelAria', { fileName: altAttr || '' })}
              title={t('embed.cancel')}
            >
              <X aria-hidden="true" size={14} />
            </button>
          ) : null}
        </span>
      </NodeViewWrapper>
    )
  }

  const attachmentBroken = attachmentId !== null && (isError || status === 403 || status === 404)
  const orphan = attachmentId === null && !srcAttr && !externalHref

  // --- 1b. EXTERNAL branch — a legacy URL embed pointing outside our origins.
  if (externalHref) {
    return (
      <NodeViewWrapper
        as="div"
        data-block-type="card-image"
        data-state="external"
        className="my-1"
        contentEditable={false}
      >
        <ExternalImageLink href={externalHref} alt={altAttr} />
      </NodeViewWrapper>
    )
  }

  // --- 2. UNAVAILABLE branch.
  if (orphan || (attachmentBroken && (srcFailed || !srcAttr))) {
    return (
      <NodeViewWrapper
        as="div"
        data-block-type="card-image"
        data-state="unavailable"
        className="my-1"
        contentEditable={false}
      >
        <span className="inline-flex items-center gap-2 rounded-md border border-border bg-bg-muted px-3 py-2 text-sm text-fg-subtle">
          <ImageOff size={16} aria-hidden="true" />
          <span>{t('embed.unavailable')}</span>
        </span>
      </NodeViewWrapper>
    )
  }

  const displayUrl = srcFailed ? signedUrl : (srcAttr ?? signedUrl)

  // --- 3. SKELETON branch — attachmentId set, URL still resolving.
  if (!displayUrl) {
    return (
      <NodeViewWrapper
        as="div"
        data-block-type="card-image"
        data-state="loading"
        className="my-1"
        contentEditable={false}
      >
        <span className="inline-flex items-center justify-center rounded-md border border-border bg-bg-muted px-4 py-6 text-sm text-fg-subtle">
          <Loader2 size={18} aria-hidden="true" className="me-2 animate-spin" />
          <span className="sr-only">{t('embed.uploading')}</span>
        </span>
      </NodeViewWrapper>
    )
  }

  // --- 4. READY branch.
  return (
    <NodeViewWrapper
      as="div"
      data-block-type="card-image"
      data-attachment-id={attachmentId ?? undefined}
      data-state="ready"
      className="my-1"
      contentEditable={false}
    >
      {/* The NodeViewWrapper carries `data-block-type="card-image"`, which
          the click-to-edit `EditableField` uses to skip its edit-flip when
          the click lands here — so opening the lightbox never enters edit
          mode. `stopPropagation` is a harmless belt-and-braces for the
          same-root case. */}
      <img
        src={displayUrl}
        alt={altAttr}
        loading="lazy"
        draggable={false}
        onError={() => {
          if (attachmentId) {
            setSrcFailed(true)
            void refresh()
          }
        }}
        onClick={(e) => {
          e.stopPropagation()
          setLightboxOpen(true)
        }}
        className={`${IMG_CLASS} cursor-zoom-in`}
        style={IMG_STYLE}
      />
      {lightboxOpen && (
        <AttachmentLightbox
          url={displayUrl}
          // `alt` carries the original filename for uploaded images. No
          // content type is passed: an `image` node holds an image by
          // definition, and the lightbox treats "URL, nothing says
          // otherwise" as an image.
          filename={altAttr || 'image'}
          attachmentId={attachmentId}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </NodeViewWrapper>
  )
}

CardImageNodeView.displayName = 'CardImageNodeView'
